import React, { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "reactstrap";

const companyListCache = new Map();
const companyListRequests = new Map();

const CompanySelector = ({
  onCompanyChange,
  onCompaniesLoaded,
  selectedCompanyId,
  showAllOption = false,
  allOptionValue = "All",
  allOptionLabel = "All",
  autoSelectDefault = true,
  className = "",
  disabled = false,
  notifyGlobalChange = true,
}) => {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");

  // Load initial company
  useEffect(() => {
    const userInfos = JSON.parse(sessionStorage.getItem("user-info"));
    const defaultId = userInfos?.user_details?.companies_data?.[0]?.company_id;

    if (selectedCompanyId !== null && selectedCompanyId !== undefined && selectedCompanyId !== "") {
      setCompanyId(selectedCompanyId);
    } else if (defaultId) {
      setCompanyId(defaultId);
      sessionStorage.setItem("active-company-id", String(defaultId));
      const initialCompany = userInfos.user_details.companies_data[0];
      if (autoSelectDefault) {
        onCompanyChange?.(initialCompany);
      }
    }
  }, [selectedCompanyId]);

  const getCompanyDetails = async () => {
    const userInfos = JSON.parse(sessionStorage.getItem("user-info"));
    const defaultId = userInfos?.user_details?.companies_data?.[0]?.company_id;
    const companyIdForList = String(companyId).toLowerCase() === "all" ? defaultId : companyId;

    if (!companyIdForList) return;
    const cacheKey = `${companyIdForList}:${showAllOption}:${allOptionValue}:${allOptionLabel}`;
    if (companyListCache.has(cacheKey)) {
      const cachedList = companyListCache.get(cacheKey);
      setCompanies(cachedList);
      onCompaniesLoaded?.(cachedList);
      return;
    }
    if (companyListRequests.has(cacheKey)) {
      try {
        const list = await companyListRequests.get(cacheKey);
        setCompanies(list);
        onCompaniesLoaded?.(list);
      } catch (error) {
        console.error("Error fetching company details:", error);
      }
      return;
    }

    const request = axios.get(`api/v1/companyv1?company_id=${companyIdForList}`)
      .then(response => {
        const response_data = response.data;
        if (!response_data?.message) return [];

        let list = response_data.message;
        if (showAllOption) {
          list = [
            { company_id: allOptionValue, company_domain: allOptionLabel },
            ...list,
          ];
        }
        companyListCache.set(cacheKey, list);
        return list;
      })
      .finally(() => companyListRequests.delete(cacheKey));

    companyListRequests.set(cacheKey, request);
    try {
      const list = await request;
      setCompanies(list);
      onCompaniesLoaded?.(list);
    } catch (error) {
      console.error("Error fetching company details:", error);
    }
  };

  useEffect(() => {
    getCompanyDetails();
  }, [companyId]);

  const handleCompanyChange = e => {
    const selectedValue = e.target.value;

    if (String(selectedValue).toLowerCase() === "all") {
      setCompanyId(allOptionValue);
      sessionStorage.removeItem("active-company-id");
      onCompanyChange?.({ company_id: allOptionValue, company_domain: allOptionLabel });
      if (notifyGlobalChange) {
        window.dispatchEvent(new CustomEvent("ats-company-changed", { detail: { company: { company_id: allOptionValue, company_domain: allOptionLabel } } }));
      }
      return;
    }

    const selectedId = Number(selectedValue);
    setCompanyId(selectedId);
    sessionStorage.setItem("active-company-id", String(selectedId));

    const selectedCompany = companies.find(
      c => String(c.company_id) === String(selectedId)
    );
    onCompanyChange?.(selectedCompany);
    if (notifyGlobalChange) {
      window.dispatchEvent(new CustomEvent("ats-company-changed", { detail: { company: selectedCompany } }));
    }
  };

  return (
    <Input
      className={`ats-company-selector ${className}`.trim()}
      type="select"
      value={companyId || ""}
      onChange={handleCompanyChange}
      disabled={disabled}
    >
      {companies.map(company => (
        <option key={company.company_id} value={company.company_id}>
          {company.company_domain}
        </option>
      ))}
    </Input>
  );
};

export default CompanySelector;
